import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import './ResetPassword.css';

const ResetPassword = () => {
  const [passwords, setPasswords] = useState({
    newPassword: '',
    verifyPassword: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [token, setToken] = useState('');
  
  const location = useLocation();

  useEffect(() => {
    // Extract token and email from URL parameters
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    const emailParam = params.get('email');
    
    if (tokenParam && emailParam) {
      setToken(tokenParam);
      // Store email for later use in the API call
      setPasswords(prev => ({
        ...prev,
        email: emailParam
      }));
    } else {
      setMessage('Invalid or missing reset token');
      setMessageType('error');
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear message when user starts typing
    if (message) {
      setMessage('');
      setMessageType('');
    }
  };

  const validatePassword = (password) => {
    // Password should be at least 8 characters with letters and numbers
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setMessage('Invalid reset token');
      setMessageType('error');
      return;
    }

    if (!passwords.newPassword || !passwords.verifyPassword) {
      setMessage('Please fill in both password fields');
      setMessageType('error');
      return;
    }

    if (passwords.newPassword !== passwords.verifyPassword) {
      setMessage('Passwords do not match');
      setMessageType('error');
      return;
    }

    if (!validatePassword(passwords.newPassword)) {
      setMessage('Password must be at least 8 characters long and contain both letters and numbers');
      setMessageType('error');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🔄 Sending password reset request...');
      console.log('Token:', token);
      console.log('Email:', passwords.email);
      
      const response = await fetch('https://tstranscareers.in/api/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Referer': window.location.href // Include current URL to extract email in backend
        },
        body: JSON.stringify({
          token: token,
          newPassword: passwords.newPassword,
          email: passwords.email
        }),
      });

      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📦 Response data:', data);

      if (response.ok) {
        console.log('✅ Password reset successful!');
        setMessage('Password reset successfully! You can now login with your new password.');
        setMessageType('success');
        
        // Show browser alert for immediate feedback
        alert('✅ Success! Your password has been reset successfully. You can now login with your new password.');
        
        // Clear the form
        setPasswords({
          newPassword: '',
          verifyPassword: '',
          email: passwords.email // Keep email for potential future use
        });
        
        // Scroll to message
        setTimeout(() => {
          document.querySelector('.message')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else {
        console.log('❌ Password reset failed:', data.message);
        setMessage(data.message || 'Failed to reset password');
        setMessageType('error');
        
        // Show error alert
        alert('❌ Error: ' + (data.message || 'Failed to reset password'));
      }
    } catch (error) {
      console.error('❌ Error resetting password:', error);
      setMessage('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h2>Reset Your Password</h2>
          <p>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={passwords.newPassword}
              onChange={handleInputChange}
              placeholder="Enter new password"
              required
              disabled={isLoading || !token}
            />
          </div>

          <div className="form-group">
            <label htmlFor="verifyPassword">Verify New Password</label>
            <input
              type="password"
              id="verifyPassword"
              name="verifyPassword"
              value={passwords.verifyPassword}
              onChange={handleInputChange}
              placeholder="Confirm new password"
              required
              disabled={isLoading || !token}
            />
          </div>

          {message && (
            <div className={`message ${messageType}`}>
              {message}
              {messageType === 'success' && (
                <div className="success-actions">
                  <Link to="/login" className="login-link">
                    ← Go to Login Page
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="reset-password-btn"
              disabled={isLoading || !token}
            >
              {isLoading ? 'Updating Password...' : 'Reset Password'}
            </button>
          </div>

          <div className="password-requirements">
            <h4>Password Requirements:</h4>
            <ul>
              <li>At least 8 characters long</li>
              <li>Contains both letters and numbers</li>
              <li>May include special characters (@$!%*?&)</li>
            </ul>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;